import React, { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useIntl } from 'react-intl';
import messages from './messages';

import { getStyles } from './styles';
import SubSidebar from './sidebar';
import { InvalidAccess } from '../../../../utils/sessionDataHandle';
import { useAppContext } from '../../../../context/AppContext';

import GeneralSettings from '../ChatbotHome';
import ChatbotDataset from '../ChatbotDataset';
import ChatbotBranding from '../ChatbotBranding';
import ChatbotAuthentication from '../ChatbotAuthentication';
import ChatbotLaunch from '../ChatbotLaunch';
import { fetchAccountId, fetchSubscriberId, fetchSubscriptionId, fetchThirdPartyToken, fetchThirdPartyTokenProvider } from '../../../../utils/GetAccountDetails';
import FilesApi from '../../../../Api/Services/AppflyteBackend/FileServiceApi';
import { tostAlert } from '../../../../utils/AlertToast';
import LoadBar from '../../../../utils/LoadBar';
import { useDispatch, useSelector } from 'react-redux';
import { setAgentApiTokenState, setAnalyticsDataSetsState } from '../../../../Redux/slice/dataSlice';
import { setAgentApiTokenAdded, setAnalyticsAppsAdded, setAnalyticsDataSetAdded } from '../../../../Redux/slice/newDataSlice';
import AnalyticsApi from '../../../../Api/Services/AmeyaBackend/AnalyticsToolApi';
import AiAppsApi from '../../../../Api/Services/AppflyteBackend/AiAppsApi';
import moment from 'moment';
import { checkProject } from '../../../../utils';
import getAnalyticsDataSetsData from '../../../../utils/ApiFunctions/AnalyticsDataSetsData';
import ChatBotLoader from '../../../../components/ChatBotLoader';
import ProjectsApi from '../../../../Api/Services/AppflyteBackend/ProjectsApi';
import {
    AdvancedChatConfig, SimpleChatConfig,
    AdvancedChatPreview, SimpleChatPreview
} from '../ChatConfigs';
import handleFileUpload from '../../../../utils/fileUpload';
import { ANALYTICS_S3_BUCKET_CHATBOT_LOGO, ANALYTICS_S3_BUCKET_FOLDER, ANALYTICS_S3_BUCKET_SCRIPT_FILE } from '../../../../utils/constants';
import { getProjectAgentApiTokens } from '../../../../utils/ApiFunctions/AgentAPiTokenData';

let bucket_name = window.__ENV__.S3_BUCKET_NAME
let s3_foldername = ANALYTICS_S3_BUCKET_FOLDER
let s3_file_name = ANALYTICS_S3_BUCKET_SCRIPT_FILE
let s3_logo_name = ANALYTICS_S3_BUCKET_CHATBOT_LOGO

function EditChatbot() {

    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const dispatch = useDispatch();
    const { app_id } = useParams()
    const intl = useIntl();
    const styles = getStyles(theme);
    const { selectedOrganization, selectedWorkspace, selectedProject, appDetails, setAppDetails, ameyaChatBotAppDetails } = useAppContext();

    const [selectedMenuItem, setSelectedMenuItem] = useState(0);
    const [tokenLoading, setTokenLoading] = useState(false);
    const [datasetLoading, setDatasetLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(false);
    const [chatbotLoading, setChatbotLoading] = useState(false);

    const [allTokens, setAllTokens] = useState([]);
    const [datasets, setDatasets] = useState([]);
    const [appAutoId, setAppAutoId] = useState(null);
    const [appUpdateKey, setAppUpdateKey] = useState(null);

    const all_agent_api_tokens = useSelector(state => state.all_data.agent_api_tokens)
    const agent_api_token_added = useSelector(state => state.data_added.agent_api_token_added)
    const analytics_data_sets = useSelector(state => state.all_data.analytics_data_sets);
    const analytics_data_set_added = useSelector(state => state.data_added.analytics_data_set_added);

    useEffect(() => {
        if (!selectedWorkspace || !selectedProject) {
            InvalidAccess(navigate);
            return
        }
        const selected_engine_name = selectedProject?.payload?.configuration?.engine_name
        if (!app_id || selected_engine_name !== "analytics_tool") {
            navigate('/ai-apps')
            return
        }
        //eslint-disable-next-line
    }, [location.pathname, navigate]);

    const handleMenuChange = (engine, path) => {
        if (engine && path) {
            navigate(`/edit-ai-app/${app_id}/${engine?.toLowerCase()?.replace(/_/g, '-')}/${path?.toLowerCase()?.replace(/_/g, '-')}`);
        }
    };

    useEffect(() => {
        getAllData()
        //eslint-disable-next-line
    }, [])

    const getAllData = async () => {
        setDataLoading(true);
        try {
            const response = await getAiAppData();
            const { app_id, default_dataset, datasets: aiDatasets = [], authTokenId, auth_type, llm_type } = response || {};

            const [tokenResponse, datasetResponse] = await Promise.all([
                getAllTokensDetails(),
                getDatasets(),
                getAppflyteAiApp(app_id)
            ]);

            const newAppDetails = {};

            if (default_dataset) {
                const defaultDataSet = datasetResponse.find(item => item?.payload?.dataset_id === default_dataset);
                if (defaultDataSet) {
                    newAppDetails.default_dataset = defaultDataSet;
                }
            }

            if (aiDatasets.length > 0) {
                const idSet = new Set(aiDatasets);
                newAppDetails.dataset = datasetResponse.filter(item => idSet.has(item?.payload?.dataset_id));
            } else {
                newAppDetails.dataset = [];
            }

            if (authTokenId) {
                const filteredToken = tokenResponse.find(item => item?.payload?.__auto_id__ === authTokenId);
                if (filteredToken?.payload) {
                    const { name, token } = filteredToken.payload;
                    newAppDetails.authConfigData = {
                        llm_type: llm_type,
                        auth_type: auth_type,
                        service_agent_token: { name, token, __auto_id__: authTokenId },
                    };
                }
            } else {
                newAppDetails.authConfigData = {
                    ...(newAppDetails.authConfigData || {}),
                    llm_type
                };
            }

            setAppDetails(prev => ({ ...prev, ...newAppDetails }));
        } catch (error) {
            console.error(error);
        } finally {
            setDataLoading(false);
        }
    };

    const getAiAppData = async () => {
        try {

            const subscriber_id = await fetchSubscriberId();
            const subscription_id = await fetchSubscriptionId();
            const project_id = selectedProject?.payload?.__auto_id__
            const file_name = `${s3_foldername}/${s3_file_name}/${subscriber_id}/${subscription_id}/${project_id}/${app_id}.js`;

            const response = await FilesApi.getDownloadUrl(bucket_name, file_name)
            if (response.status === 200) {

                const logo_res_data = response?.data?.at(-1) ?? null;
                const download_url = Object.values(logo_res_data)?.at(-1) ?? null;
                const configData = await fetch(download_url);
                const scriptText = await configData.text();
                const configMatch = scriptText.match(/const\s+chatbotConfig\s*=\s*(\{[\s\S]*?\})\s*;?\s*\/\/\s*END\s+chatbotConfig/);

                if (configMatch && configMatch[1]) {
                    const chatbotConfig = JSON.parse(configMatch[1]) ?? {};
                    const app_id = chatbotConfig?.APP_DETAILS?.app_id ?? '';
                    const app_name = chatbotConfig?.APP_DETAILS?.app_name ?? '';
                    const app_description = chatbotConfig?.APP_DETAILS?.app_description ?? '';
                    const app_launch_icon = chatbotConfig?.APP_DETAILS?.app_launch_icon ?? '';
                    const app_bot_type = chatbotConfig?.APP_DETAILS?.app_bot_type ?? '';
                    const welcome_message = chatbotConfig?.WELCOME_MESSAGE ?? '';
                    const app_logo_name = chatbotConfig?.APP_DETAILS?.app_logo_name ?? null;
                    const feedback_status = chatbotConfig?.APP_DETAILS?.feedback_status ?? null;
                    const feedback_url = chatbotConfig?.APP_DETAILS?.feedback_url ?? null;

                    const datasets = chatbotConfig?.SOURCE_DETAILS?.datasets ?? [];
                    const default_dataset = chatbotConfig?.SOURCE_DETAILS?.default_dataset ?? null;

                    const service_agent_user_token_id = chatbotConfig?.AUTHENTICATION_CONFIGS?.service_agent_user_token_id ?? null;
                    const auth_type = chatbotConfig?.AUTHENTICATION_CONFIGS?.auth_type ?? 'auth_type';
                    const llm_type = chatbotConfig?.AUTHENTICATION_CONFIGS?.llm_type ?? 'gemini';

                    const chatStyleDetails = {
                        font_family: chatbotConfig?.UI_CONFIGS?.font_family ?? "Inter",
                        font_color: chatbotConfig?.UI_CONFIGS?.font_color ?? '#000000',
                        background_color: chatbotConfig?.UI_CONFIGS?.background_color ?? '#000000',
                        surface_color: chatbotConfig?.UI_CONFIGS?.surface_color ?? '#ffffff',
                        surface_border: chatbotConfig?.UI_CONFIGS?.surface_border ?? '#ffffff',
                        button_fill: chatbotConfig?.UI_CONFIGS?.button_fill ?? '#000000',
                        button_text: chatbotConfig?.UI_CONFIGS?.button_text ?? '#000000',
                        question_bubble_color: chatbotConfig?.UI_CONFIGS?.question_bubble_color ?? '#000000',
                        question_bubble_font_color: chatbotConfig?.UI_CONFIGS?.question_bubble_font_color ?? '#ffffff',
                        poweredby_logo_status: chatbotConfig?.UI_CONFIGS?.poweredby_logo_status ?? true
                    }

                    const appLogoDetails = {
                        selectedLogo: null,
                        s3_url: app_logo_name,
                        previewUrl: null
                    }

                    const chatFeedbackDetails = {
                        feedback_status: feedback_status ? "true" : "false",
                        feedback_url: feedback_url
                    }

                    setAppDetails({
                        ...appDetails,
                        app_id: app_id,
                        app_name: app_name,
                        app_description: app_description,
                        app_launch_icon: app_launch_icon,
                        app_bot_type: app_bot_type,
                        welcome_message: welcome_message,
                        chatStylesData: chatStyleDetails,
                        appLogoData: appLogoDetails,
                        chatFeedbackData: chatFeedbackDetails
                    })

                    return {
                        'app_id': app_id,
                        'datasets': datasets,
                        'default_dataset': default_dataset,
                        'auth_type': auth_type,
                        'authTokenId': service_agent_user_token_id,
                        'llm_type': llm_type
                    }
                } else {
                    tostAlert(intl.formatMessage(messages.configuration_error), 'warning');
                    navigate('/ai-apps')
                    return {
                        'app_id': null,
                        'datasets': [],
                        'default_dataset': null,
                        'authTokenId': null,
                        'auth_type': 'auth',
                        'llm_type': 'gemini'
                    }
                }
            }
        } catch (error) {
            console.log(error)
        }
    }

    const getAllTokensDetails = async () => {
        setTokenLoading(true)
        let all_tokens = []
        try {
            if (all_agent_api_tokens?.length > 0 && agent_api_token_added === false) {
                all_tokens = all_agent_api_tokens
            } else {
                const organization_id = selectedOrganization?.payload?.__auto_id__;
                const project_id = selectedProject?.payload?.__auto_id__;
                const response = await getProjectAgentApiTokens(organization_id, project_id);
                all_tokens = response
                dispatch(setAgentApiTokenState(response))
                dispatch(setAgentApiTokenAdded(false))
            }
            const filtered_tokens = (all_tokens || []).filter(token => token?.payload?.token_type === "admin_generate_token") ?? [];
            setAllTokens(filtered_tokens)
            return filtered_tokens;

        } catch (error) {
            console.log(error)
            return
        } finally {
            setTokenLoading(false)
        }
    }

    const getDatasets = async () => {
        setDatasetLoading(true)
        try {
            if ((analytics_data_sets || [])?.length > 0 && analytics_data_set_added === false) {
                setDatasets(analytics_data_sets)
                return analytics_data_sets
            } else {
                const response = await getAnalyticsDataSetsData(selectedProject?.payload?.__auto_id__);
                dispatch(setAnalyticsDataSetsState(response))
                dispatch(setAnalyticsDataSetAdded(false))
                setDatasets(response)
                return response
            }
        } catch (error) {
            console.log(error)
        } finally {
            setDatasetLoading(false)
        }
    }

    const getAppflyteAiApp = async (app_id) => {
        try {
            const response = await AiAppsApi.getAiAppById(app_id);
            const responseData = response?.data ?? null;
            if (responseData) {
                const __auto_id__ = responseData?.payload?.__auto_id__ ?? null;
                const update_key = responseData?.update_key ?? null;
                setAppAutoId(__auto_id__);
                setAppUpdateKey(update_key)
                return
            }
        } catch (error) {
            console.log(error)
            return null
        }
    }

    const updateApp = async (app_name, app_description, app_logo, datasetIds) => {
        try {
            const appObj = {
                "id": appAutoId,
                "fields": [
                    { "path": "$.name", "value": app_name },
                    { "path": "$.description", "value": app_description },
                    { "path": "$.app_logo", "value": app_logo },
                    { "path": "$.datasets", "value": datasetIds },
                    { "path": "$.updated_on", "value": moment().format("DD-MM-YYYY HH:mm:ss") },
                ],
            }
            const response = await AiAppsApi.updateAiApp(JSON.stringify(appObj), appAutoId, appUpdateKey)
            if (response.status === 200) {
                return true
            }
            return false
        } catch (error) {
            console.log(error)
            return false
        }
    }

    function updateExtractionProjects(extraction_projects = [], an_project_id, app_id) {
        (async () => {
            const updates = extraction_projects.map(project_id => {

                const payload = {
                    id: project_id,
                    fields: [
                        { path: "$.analytics_app_id", value: app_id },
                        { path: "$.analytics_project_id", value: an_project_id }
                    ]
                };

                return ProjectsApi.updateProject(JSON.stringify(payload), project_id, null);
            });
            await Promise.allSettled(updates);
        })();
    }

    const handleSave = async (save_from) => {
        setDataLoading(true);
        try {
            const app_id = appDetails?.app_id ?? '';
            const app_name = appDetails?.app_name ?? '';
            const app_description = appDetails?.app_description ?? '';
            const app_bot_type = appDetails?.app_bot_type ?? 'simple_bot';
            const app_launch_icon = appDetails?.app_launch_icon ?? 'chatlaunch1';
            const selectedLogo = appDetails?.appLogoData?.selectedLogo ?? null;
            const welcome_message = appDetails?.welcome_message ?? '';
            const default_dataset = appDetails?.default_dataset?.payload?.dataset_id ?? null;
            const project_id = selectedProject?.payload?.__auto_id__;
            const llm_type = appDetails?.authConfigData?.llm_type ?? "gemini";

            if (!app_name) {
                tostAlert(intl.formatMessage(messages.field_check), 'warning')
                navigate(`/edit-ai-app/${app_id}/analytics-tool/chatbot-home`)
                return
            }

            const dataset = appDetails?.dataset || [];
            const datasetIds = (dataset || [])?.map(item => item?.payload?.dataset_id) ?? [];
            const extraction_projects = (dataset || []).flatMap(item => item?.payload?.extraction_projects ?? []);


            if (dataset?.length === 0 || datasetIds?.length === 0) {
                tostAlert(intl.formatMessage(messages.field_check), 'warning')
                navigate(`/edit-ai-app/${app_id}/analytics-tool/datasets`)
                return
            }

            const { ...rest } = appDetails?.chatStylesData ?? {};
            if (Object.values(rest).some(v => v === null || v === undefined || v === "")) {
                tostAlert(intl.formatMessage(messages.field_check), 'warning');
                navigate(`/edit-ai-app/${app_id}/analytics-tool/branding`);
                return null;
            }

            const feedback_status = appDetails?.chatFeedbackData?.feedback_status === "true";
            const feedback_url = appDetails?.chatFeedbackData?.feedback_url ?? "";
            if (feedback_status && !feedback_url.trim()) {
                tostAlert(intl.formatMessage(messages.field_check), 'warning')
                navigate(`/edit-ai-app/${app_id}/analytics-tool/branding`);
                return
            }

            let auth_type = appDetails?.authConfigData?.auth_type ?? "no_auth";
            if (app_bot_type === 'simple_bot') {
                auth_type = "no_auth";
            }

            const service_agent_token = appDetails?.authConfigData?.service_agent_token ?? null;
            if (!service_agent_token) {
                tostAlert(intl.formatMessage(messages.field_check), 'warning')
                navigate(`/edit-ai-app/${app_id}/analytics-tool/security`)
                return
            }

            const account_id = await fetchAccountId();
            const subscriber_id = await fetchSubscriberId();
            const subscription_id = await fetchSubscriptionId();

            const appflyte_account_configs = {
                account_id: account_id,
                subscriber_id: subscriber_id,
                subscription_id: subscription_id,
                schema_id: window.__ENV__.AMEYA_COLLECTIONS_SCHEMA_ID,
                project_id: project_id
            }

            const api_url_config = {
                appflyte_backend_base_url: window.__ENV__.APPFLYTE_BACKEND_URL,
                analytics_base_url: window.__ENV__.ANALYTICS_SERVICE_URL
            }

            let app_logo_name = appDetails?.appLogoData?.s3_url ?? null;
            if (selectedLogo) {
                const file_type = selectedLogo?.name?.split('.')[1];
                app_logo_name = `${s3_foldername}/${s3_logo_name}/${subscriber_id}/${subscription_id}/${project_id}/${app_id}.${file_type}`;
                const logo_response = await handleFileUpload(app_logo_name, selectedLogo);
                if (logo_response.status !== 200) {
                    tostAlert(intl.formatMessage(messages.update_error), 'error');
                    return
                }
            }

            if (save_from === "general" || save_from === "datasets") {
                const appObj = {
                    "app_id": app_id,
                    "app_name": app_name,
                    "app_description": app_description,
                    "dataset_ids": datasetIds
                }
                const ai_app_response = await AnalyticsApi.updateAiApp(JSON.stringify(appObj));
                if (ai_app_response.status !== 200) {
                    tostAlert(intl.formatMessage(messages.update_error), 'error');
                    return
                }
            }

            const dpod_ai_app = await updateApp(app_name, app_description, app_logo_name, datasetIds);
            if (!dpod_ai_app) {
                tostAlert(intl.formatMessage(messages.update_error), 'error');
                return
            }

            (extraction_projects || [])?.length > 0 && updateExtractionProjects(extraction_projects, project_id, app_id)

            const chatbotConfig = {
                WELCOME_MESSAGE: welcome_message || "",
                APP_DETAILS: {
                    app_id: app_id || "",
                    app_name: app_name || "",
                    app_description: app_description || "",
                    app_bot_type: app_bot_type,
                    app_launch_icon: app_launch_icon,
                    app_logo_name: app_logo_name || "",
                    feedback_status: feedback_status,
                    feedback_url: feedback_url
                },
                APPFLYTE_ACCOUNT_CONFIGS: appflyte_account_configs || {},
                API_URL_CONFIGS: api_url_config || {},
                SOURCE_DETAILS: {
                    default_dataset: default_dataset,
                    datasets: datasetIds
                },
                UI_CONFIGS: appDetails?.chatStylesData || {},
                AUTHENTICATION_CONFIGS: {
                    auth_type: auth_type,
                    llm_type: llm_type,
                    service_agent_user_token_id: service_agent_token?.__auto_id__ || "",
                    service_agent_user_token: service_agent_token?.token || ""
                }
            }

            const chatEmbedd_file = app_bot_type === "simple_bot" ? SimpleChatConfig(chatbotConfig) : AdvancedChatConfig(chatbotConfig);
            const blob = new Blob([chatEmbedd_file], { type: "application/javascript" });
            const filename = `${s3_foldername}/${s3_file_name}/${subscriber_id}/${subscription_id}/${project_id}/${app_id}.js`;
            const file = new File([blob], filename, { type: "application/javascript" });
            const response = await handleFileUpload(filename, file);
            if (response.status === 200) {
                tostAlert(intl.formatMessage(messages.update_success), 'success');
                dispatch(setAnalyticsAppsAdded(true))
                navigate('/ai-apps')
            } else {
                tostAlert(intl.formatMessage(messages.update_error), 'error');
                dispatch(setAnalyticsAppsAdded(true))
                navigate('/ai-apps')
            }
        } catch (error) {
            console.log(error)
        } finally {
            setDataLoading(false);
        }
    }

    function handleCancel() {
        navigate('/ai-apps')
        setAppDetails(ameyaChatBotAppDetails)
    }

    const loadChatBot = async (chatBotData) => {
        try {
            const scriptId = "analytics-script";
            const existingScript = document.getElementById(scriptId);

            if (existingScript) {
                console.log("Removing existing analytics script...");
                existingScript.remove();
            }

            const app_bot_type = appDetails?.app_bot_type ?? 'simple_bot';
            console.log('Loading chatbot of type:', app_bot_type);
            let newScript = null;
            if (app_bot_type === 'advanced_bot') {
                newScript = await AdvancedChatPreview(chatBotData);
            } else {
                newScript = await SimpleChatPreview(chatBotData);
            }

            const script = document.createElement("script");
            script.id = scriptId;
            script.type = "text/javascript";
            script.textContent = newScript;
            document.body.appendChild(script);

            return script;
        } catch (error) {
            console.error("Error loading chatbot:", error);
            return null;
        }
    };

    const handleChatbotToggle = async () => {
        setChatbotLoading(true)
        try {
            const third_party_token = await fetchThirdPartyToken();
            const token_provider = await fetchThirdPartyTokenProvider()

            const app_id = appDetails?.app_id ?? '';
            const app_name = appDetails?.app_name ?? '';
            const app_description = appDetails?.app_description ?? '';
            const app_bot_type = appDetails?.app_bot_type ?? 'simple_bot';
            const app_launch_icon = appDetails?.app_launch_icon ?? 'app_launch_icon';
            const selectedLogo = appDetails?.appLogoData?.selectedLogo ?? null;
            const welcome_message = appDetails?.welcome_message ?? '';
            const llm_type = appDetails?.authConfigData?.llm_type ?? "gemini";

            const account_id = await fetchAccountId();
            const subscriber_id = await fetchSubscriberId();
            const subscription_id = await fetchSubscriptionId();
            const project_id = selectedProject?.payload?.__auto_id__;
            const default_dataset = appDetails?.default_dataset?.payload?.dataset_id ?? null;
            const dataset = appDetails?.dataset || [];
            const datasetIds = (dataset || [])?.map(item => item?.payload?.dataset_id) ?? [];

            if (!app_name) {
                tostAlert(intl.formatMessage(messages.field_check), 'warning')
                navigate(`/edit-ai-app/${app_id}/analytics-tool/chatbot-home`)
                return
            }

            if (dataset?.length === 0 || datasetIds?.length === 0) {
                tostAlert(intl.formatMessage(messages.field_check), 'warning')
                navigate(`/edit-ai-app/${app_id}/analytics-tool/datasets`)
                return
            }

            const feedback_status = appDetails?.chatFeedbackData?.feedback_status === "true";
            const feedback_url = appDetails?.chatFeedbackData?.feedback_url ?? "";
            if (feedback_status && !feedback_url.trim()) {
                tostAlert(intl.formatMessage(messages.field_check), 'warning')
                navigate(`/edit-ai-app/${app_id}/analytics-tool/branding`);
                return
            }

            let auth_type = appDetails?.authConfigData?.auth_type ?? "no_auth";
            if (app_bot_type === 'simple_bot') {
                auth_type = "no_auth";
            }

            const service_agent_token = appDetails?.authConfigData?.service_agent_token ?? null;
            if (!service_agent_token) {
                tostAlert(intl.formatMessage(messages.field_check), 'warning')
                navigate(`/edit-ai-app/${app_id}/analytics-tool/security`)
                return
            }

            await checkProject(project_id);

            if (!third_party_token) {
                tostAlert(intl.formatMessage(messages.auth_error), 'warning')
                return;
            }

            let app_logo_name = appDetails?.appLogoData?.s3_url ?? null;
            if (selectedLogo) {
                const file_type = selectedLogo?.name?.split('/')?.pop()?.split('.')?.pop() ?? null;
                if (!file_type) {
                    console.log('Invalid file')
                    tostAlert(intl.formatMessage(messages.lauch_error), 'error');
                }

                app_logo_name = `${s3_foldername}/${s3_logo_name}/${subscriber_id}/${subscription_id}/${project_id}/${app_id}.${file_type}`;
                const logo_response = await handleFileUpload(app_logo_name, selectedLogo);
                if (logo_response.status !== 200) {
                    tostAlert(intl.formatMessage(messages.lauch_error), 'error');
                    return
                }
            }

            const chatBotData = {
                "WELCOME_MESSAGE": welcome_message,
                "APP_DETAILS": {
                    "app_id": app_id,
                    "app_name": app_name,
                    "app_description": app_description,
                    "app_bot_type": app_bot_type,
                    "app_launch_icon": app_launch_icon,
                    "app_logo_name": app_logo_name,
                    "feedback_status": feedback_status,
                    "feedback_url": feedback_url
                },

                "APPFLYTE_ACCOUNT_CONFIGS": {
                    "account_id": account_id,
                    "subscriber_id": subscriber_id,
                    "subscription_id": subscription_id,
                    "schema_id": window.__ENV__.AMEYA_COLLECTIONS_SCHEMA_ID,
                    "project_id": project_id
                },
                "API_URL_CONFIGS": {
                    "appflyte_backend_base_url": window.__ENV__.APPFLYTE_BACKEND_URL,
                    "analytics_base_url": window.__ENV__.ANALYTICS_SERVICE_URL
                },
                "SOURCE_DETAILS": {
                    "default_dataset": default_dataset,
                    "datasets": datasetIds
                },
                "UI_CONFIGS": appDetails?.chatStylesData || {},
                "AUTHENTICATION_CONFIGS": {
                    "auth_type": auth_type,
                    "llm_type": llm_type,
                    "service_agent_user_token_id": service_agent_token?.__auto_id__,
                    "service_agent_user_token": service_agent_token?.token
                },
            }
            const script_load = await loadChatBot(chatBotData);
            if (!script_load) {
                tostAlert(intl.formatMessage(messages.lauch_error), 'warning');
            }
            else {
                if (app_bot_type === 'advanced_bot') {
                    setTimeout(() => {
                        if (window.toggleChatbotPreview) {
                            window.toggleChatbotPreview(token_provider, third_party_token);
                        } else {
                            tostAlert(intl.formatMessage(messages.lauch_error), 'warning');
                        }
                    }, 50);
                }
            }
        } catch (error) {
            console.log(error)
        } finally {
            setChatbotLoading(false)
        }
    }

    return (
        <Box sx={styles.mainContainer}>

            {(dataLoading) && <LoadBar />}
            {chatbotLoading && <ChatBotLoader />}

            <Box sx={styles.sidebar}>
                <SubSidebar
                    handleMenuChange={handleMenuChange}
                    setSelectedMenuItem={setSelectedMenuItem}
                    selectedMenuItem={selectedMenuItem}
                />
            </Box>
            <main style={styles.componentContainer}>
                <Box sx={styles.cardContainer}>
                    <Routes>
                        <Route path="/" element={<Navigate to="analytics-tool/chatbot-home" replace />} />
                        <Route
                            path="analytics-tool/chatbot-home"
                            element={<GeneralSettings
                                dataLoading={dataLoading}
                                action_type={'update'}
                                onSave={handleSave}
                                onCancel={handleCancel}
                            />}
                        />
                        <Route
                            path="analytics-tool/datasets"
                            element={<ChatbotDataset
                                action_type={'update'}
                                dataLoading={dataLoading}
                                datasets={datasets}
                                onSave={handleSave}
                                getDatasets={getDatasets}
                                datasetLoading={datasetLoading}
                                onCancel={handleCancel}
                            />}
                        />
                        <Route
                            path="analytics-tool/branding"
                            element={<ChatbotBranding
                                dataLoading={dataLoading}
                                action_type={'update'}
                                onSave={handleSave}
                                onCancel={handleCancel}
                            />}
                        />
                        <Route path="analytics-tool/security"
                            element={<ChatbotAuthentication
                                dataLoading={dataLoading}
                                action_type={'update'}
                                allTokens={allTokens}
                                datasets={datasets}
                                getAllTokensDetails={getAllTokensDetails}
                                tokenLoading={tokenLoading}
                                onSave={handleSave}
                                onCancel={handleCancel}
                            />}
                        />
                        <Route
                            path="analytics-tool/test-launch"
                            element={<ChatbotLaunch
                                action_type={'update'}
                                dataLoading={dataLoading}
                                onSave={handleSave}
                                onChatbotToggle={handleChatbotToggle}
                                onCancel={handleCancel}
                            />}
                        />
                    </Routes>
                </Box>
            </main>
        </Box>
    )
}

export default EditChatbot;